// The app's three top-level surfaces.
//
// Everything else here is modal state, but Lisa asked for Your Stories as a
// PAGE, so it needs a URL someone can send to a friend. A real path rather than
// a ?page= param: public/_redirects already serves index.html for /* on Netlify,
// so /stories loads directly, and a path is the version a search engine will
// index as its own page.
//
// Groups joined it for the same reason. It was a modal, which was the right size
// for the five entries it launched with; at twenty-five it is a list someone
// would send to a friend who just moved here, and a modal has no URL to send.
//
// Vendors is the same move again (Lisa, 8/19): "Hosts and Vendors" is a page
// someone posting a market would bookmark or send to a vendor they know, not a
// thing that fits inside a filter toggle.
//
// The calendar's filters stay in the query string (see urlFilters); none of the
// other three has anything to filter, so they carry bare paths.

import { useCallback, useEffect, useState } from 'react'

export type RouteName = 'calendar' | 'stories' | 'groups' | 'resources' | 'vendors'

const PATHS: Record<RouteName, string> = {
  calendar: '/',
  stories: '/stories',
  groups: '/groups',
  resources: '/resources',
  vendors: '/vendors',
}

export function readRoute(): RouteName {
  if (typeof window === 'undefined') return 'calendar'
  // Trailing slashes collapse, so /stories/ is the same page as /stories.
  const path = window.location.pathname.replace(/\/+$/, '')
  if (path === '/stories') return 'stories'
  if (path === '/groups') return 'groups'
  if (path === '/resources') return 'resources'
  if (path === '/vendors') return 'vendors'
  return 'calendar'
}

export function useRoute(): [RouteName, (next: RouteName) => void] {
  const [route, setRoute] = useState<RouteName>(readRoute)

  // Back and forward have to work, or the browser's own back button strands
  // someone on a page the app thinks it already left.
  useEffect(() => {
    const onPop = () => setRoute(readRoute())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = useCallback((next: RouteName) => {
    if (readRoute() === next) return
    // pushState, not replaceState: moving between two surfaces is exactly the
    // kind of step back should undo. (The filter writes use replaceState for the
    // opposite reason - a history entry per checkbox would bury the button.)
    //
    // Returning to the calendar keeps the current query string so the filters
    // someone had set are still there; leaving for /stories or /groups drops it,
    // since none of those params mean anything on either page.
    const url = next === 'calendar' ? `/${window.location.search}` : PATHS[next]
    window.history.pushState(null, '', url)
    setRoute(next)
    window.scrollTo(0, 0)
  }, [])

  return [route, go]
}
